import { ScheduleSendIcon } from "assets/icons/ScheduleSend";
import { events } from "constants/tagManager";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import dayjs from "dayjs";
import { useAnalytics } from "hooks/useAnalytics";
import useFormValidation from "hooks/useFormValidation";
import {
  validateScheduledFirstCommunicationDate,
  mapFormToRequestBody,
} from "pages/CreateOrder/utils/helpers";
import { Button } from "primereact/button";
import { CalendarValueType } from "primereact/calendar";
import { useContext, useMemo, useEffect, useState } from "react";
import { useFormContext, useFormState, Controller } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "store";
import {
  useApproveDraftOrderMutation,
  useCreateOrdersMutation,
  useRedoOrderMutation,
} from "store/queries/order";
import { cleanUpServiceDetails } from "store/slices/serviceDetails";
import styled from "styled-components";

import { ScheduleDateTimeDialog } from "./components/ScheduleDateTimeDialog";

const StyledButton = styled(Button)<{ scheduled: boolean }>`
  padding: 0 !important;
`;

const StyledDeleteButton = styled(Button)`
  padding: 0 !important;
`;

export function Submit({
  draftOrder,
  redo = false,
}: {
  draftOrder?: boolean;
  redo?: boolean;
}) {
  const dispatch = useDispatch();
  const [useCreateOrders] = useCreateOrdersMutation();
  const [useApproveDraft] = useApproveDraftOrderMutation();
  const [redoOrder] = useRedoOrderMutation();
  const { setIsLoading } = useLoading();
  const [showScheduleDateTimeDialog, setShowScheduleDateTimeDialog] =
    useState(false);

  const { loadingPriceBreakdown } = useSelector(
    (state: RootState) => state.serviceDetailsSlice
  );
  const { pageViewEvents } = useAnalytics();

  const { current: toastElement } = useContext(ToastContext);
  const {
    getValues,
    watch,
    trigger,
    formState: { errors },
    control,
    setValue,
  } = useFormContext();
  const { isValid } = useFormState();
  const navigate = useNavigate();
  const { getFormErrorMessage } = useFormValidation();

  const procedures = watch("serviceDetails");
  const dateOfService = watch("dateOfService");
  const scheduledFirstCommunicationSent = watch(
    "scheduledFirstCommunicationSent"
  );
  const scheduledFirstCommunicationDate = watch(
    "scheduledFirstCommunicationDate"
  );
  const [selectedDate, setSelectedDate] = useState<CalendarValueType>(
    scheduledFirstCommunicationDate ?? dayjs().add(2, "minute").toDate()
  );

  useEffect(() => {
    validateScheduledFirstCommunicationDate(
      scheduledFirstCommunicationDate,
      dateOfService,
      scheduledFirstCommunicationSent
    );
    trigger("scheduledFirstCommunicationDate");
  }, [
    scheduledFirstCommunicationDate,
    dateOfService,
    scheduledFirstCommunicationSent,
  ]);

  const isButtonDisabled = useMemo(() => {
    const hasEmptyCptDescription = getValues()?.serviceDetails?.some(
      (procedure: any) => !procedure.cptDescription.trim().length
    );
    return !isValid || !procedures?.length || hasEmptyCptDescription;
  }, [isValid, procedures]);

  const showCalendar = useMemo(() => {
    if (redo && !scheduledFirstCommunicationDate) {
      return false;
    }
    return true;
  }, [scheduledFirstCommunicationSent]);

  const viewCreatedOrder = (id: string) => {
    navigate(`/orders/${id}`);
  };

  const createOrder = async (saveAsDraft = false) => {
    const isValid = await trigger("scheduledFirstCommunicationDate");
    if (!isValid) {
      return;
    }
    setIsLoading(true);
    const requestBody = mapFormToRequestBody(getValues(), saveAsDraft);
    useCreateOrders(requestBody)
      .unwrap()
      .then((createdOrder) => {
        pageViewEvents(
          {
            accountName: createdOrder.data.account.name,
            orderType: createdOrder.data.orderType,
            communicationMethod:
              createdOrder.data.patient.preferredContactMethod,
          },
          events.ORDER_CREATED
        );

        setIsLoading(false);
        dispatch(cleanUpServiceDetails());

        toastElement?.show({
          severity: "success",
          summary: "Order created",
          detail: "Order created successfully",
          content: (
            <div className="card flex flex-wrap justify-content-center gap-3 text-center">
              <h3>{saveAsDraft ? "Draft Order Created" : "Order Created!"}</h3>
              <p>
                {saveAsDraft
                  ? "Draft order has been successfully created"
                  : `Your order has been successfully created! The patient has been
                sent communication for payment collection.`}
              </p>
              <p className="w-full">
                {saveAsDraft
                  ? "Select to view the order"
                  : "To begin collecting payment, select to view the order."}
              </p>
              <Button
                label="View Order"
                onClick={() => viewCreatedOrder(createdOrder.data.id)}
              />
            </div>
          ),
        });
        navigate(`/hospitals/view/${createdOrder.data.account.id}/orders`);
      })
      .catch(() => {
        setIsLoading(false);

        toastElement?.show({
          severity: "error",
          summary: "Error",
          detail: "An error has occurred attempting to create the Order",
        });
      });
  };

  const updateOrder = async () => {
    const isValid = await trigger("scheduledFirstCommunicationDate");
    if (!isValid) {
      return;
    }
    setIsLoading(true);
    const requestBody = mapFormToRequestBody(getValues());

    useApproveDraft(requestBody)
      .unwrap()
      .then((createdOrder) => {
        pageViewEvents(
          {
            accountName: createdOrder.data.account.name,
            orderType: createdOrder.data.orderType,
            communicationMethod:
              createdOrder.data.patient.preferredContactMethod,
          },
          events.ORDER_CREATED
        );

        setIsLoading(false);
        dispatch(cleanUpServiceDetails());

        toastElement?.show({
          severity: "success",
          summary: "Order approved",
          detail: "Order approved successfully",
        });
        navigate(`/hospitals/view/${createdOrder.data.account.id}/orders`);
      })
      .catch(() => {
        setIsLoading(false);

        toastElement?.show({
          severity: "error",
          summary: "Error",
          detail: "An error has occurred attempting to create the Order",
        });
      });
  };

  const handleRedoOrder = async (saveAsDraft = false) => {
    const isValid = await trigger("scheduledFirstCommunicationDate");
    if (!isValid) {
      return;
    }
    if (
      saveAsDraft &&
      !scheduledFirstCommunicationSent &&
      scheduledFirstCommunicationDate
    ) {
      setValue("scheduledFirstCommunicationDate", null);
      setValue("scheduledFirstCommunicationSent", false);
    }
    setIsLoading(true);
    const requestBody = { ...mapFormToRequestBody(getValues()), saveAsDraft };
    redoOrder(requestBody)
      .unwrap()
      .then((redoneOrder) => {
        pageViewEvents(
          {
            accountName: redoneOrder.data.account.name,
            orderType: redoneOrder.data.orderType,
            communicationMethod:
              redoneOrder.data.patient.preferredContactMethod,
          },
          events.ORDER_REDONE
        );

        setIsLoading(false);
        dispatch(cleanUpServiceDetails());

        toastElement?.show({
          severity: "success",
          summary: "Order redone",
          detail: "Order redone successfully",
          content: (
            <div className="card flex flex-wrap justify-content-center gap-3 text-center">
              <h3>{saveAsDraft ? "Order Status Changed" : "Order Redone!"}</h3>
              <p>
                {saveAsDraft
                  ? `Your order has been successfully saved as draft`
                  : `Your order has been successfully redone! The patient has been
                sent updated communication for payment collection.`}
              </p>
              <p>
                {saveAsDraft
                  ? "Select to view the order"
                  : "To begin collecting payment, select to view the order."}
              </p>
              <Button
                label="View Order"
                onClick={() => viewCreatedOrder(redoneOrder.data.id)}
              />
            </div>
          ),
        });
        navigate(`/hospitals/view/${redoneOrder.data.account.id}/orders`);
      })
      .catch(() => {
        setIsLoading(false);

        toastElement?.show({
          severity: "error",
          summary: "Error",
          detail: "An error has occurred attempting to redo the Order",
        });
      });
  };

  const handleButtonClick = async () => {
    const isValid = await trigger("scheduledFirstCommunicationDate");
    if (!isValid) {
      return;
    }
    if (draftOrder) {
      updateOrder();
      return;
    }
    if (redo) {
      handleRedoOrder();
      return;
    }
    createOrder();
  };

  const createButtonText = useMemo(() => {
    if (redo) {
      return "Save Changes";
    }
    return scheduledFirstCommunicationDate
      ? "Create and Save"
      : "Create and Send";
  }, [redo, scheduledFirstCommunicationDate]);

  return (
    <div className="flex flex-column">
      <div className="flex justify-content-between flex-column md:flex-row gap-2">
        <ScheduleDateTimeDialog
          onClose={() => setShowScheduleDateTimeDialog(false)}
          visible={showScheduleDateTimeDialog}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          onSave={handleButtonClick}
        />
        {!draftOrder && (
          <div
            data-testid="saveDraft_ReviewOrder"
            className="flex flex-1 md:justify-content-start justify-content-center pb-3"
          >
            <Button
              label="Save Draft"
              onClick={() => (redo ? handleRedoOrder(true) : createOrder(true))}
              loading={loadingPriceBreakdown}
              disabled={isButtonDisabled}
            />
          </div>
        )}
        <div
          data-testid="submit"
          className="flex flex-1 justify-content-center md:justify-content-end pb-3"
        >
          <Button
            label={createButtonText}
            onClick={handleButtonClick}
            disabled={isButtonDisabled}
            loading={loadingPriceBreakdown}
          />
        </div>
      </div>
      {showCalendar && (
        <div className="flex flex-column align-items-end">
          <div className="flex">
            <StyledButton
              className="p-button-text"
              scheduled={!!scheduledFirstCommunicationDate}
              disabled={scheduledFirstCommunicationSent || isButtonDisabled}
              icon={
                scheduledFirstCommunicationDate ? (
                  "pi pi-clock"
                ) : (
                  <ScheduleSendIcon
                    width={20}
                    height={20}
                    color="#5b4dfd"
                    className="mr-2"
                  />
                )
              }
              onClick={() => setShowScheduleDateTimeDialog(true)}
            >
              {!scheduledFirstCommunicationDate && "Schedule send"}
              {scheduledFirstCommunicationDate && (
                <span className="ml-2">
                  Scheduled
                  <span className="ml-1 text-sm">
                    (
                    {dayjs(scheduledFirstCommunicationDate).format(
                      "MM/DD/YYYY hh:mm A"
                    )}
                    )
                  </span>
                </span>
              )}
            </StyledButton>
            {scheduledFirstCommunicationDate && (
              <StyledDeleteButton
                className="p-button-text p-button-danger max-w-12rem"
                onClick={() => {
                  setValue("scheduledFirstCommunicationDate", null);
                  setSelectedDate(dayjs().add(2, "minute").toDate());
                }}
                disabled={scheduledFirstCommunicationSent}
                icon="pi pi-trash"
              />
            )}
          </div>
          {getFormErrorMessage("scheduledFirstCommunicationDate", errors)}
          <Controller
            data-testid="scheduledFirstCommunicationDate"
            name="scheduledFirstCommunicationDate"
            control={control}
            shouldUnregister={false}
            rules={{
              validate: (value) =>
                validateScheduledFirstCommunicationDate(
                  value,
                  dateOfService,
                  scheduledFirstCommunicationSent
                ),
            }}
            // No visible UI here — this just keeps the field alive
            render={() => <div className="hidden">test</div>}
          />
        </div>
      )}
    </div>
  );
}
